import { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { DemoBadge } from "@/components/DemoBadge";
import { BrandLogo } from "@/lib/brand";
import { NotificationBell } from "@/components/NotificationBell";
import {
  Home, Building2, ClipboardPlus, ClipboardCheck, Camera, GraduationCap,
  ListTodo, MapPinned, TrendingUp, FileText, UserCircle, LogOut, Menu, X, Target as TargetIcon, Lightbulb, LayoutGrid, Truck,
} from "lucide-react";

const BOTTOM_NAV = [
  { to: "/", icon: Home, label: "Dashboard" },
  { to: "/customers", icon: Building2, label: "Customers" },
  { to: "/visits/new", icon: ClipboardPlus, label: "New Visit", cta: true },
  { to: "/visits", icon: ClipboardCheck, label: "Visits" },
  { to: "/tasks", icon: ListTodo, label: "Tasks" },
];

const DRAWER_NAV = [
  { to: "/", icon: Home, label: "My Dashboard" },
  { to: "/customers", icon: Building2, label: "My Customers" },
  { to: "/visits/new", icon: ClipboardPlus, label: "Start Store Visit" },
  { to: "/visits", icon: ClipboardCheck, label: "My Visits" },
  { to: "/photos", icon: Camera, label: "Upload Photos" },
  { to: "/training/new", icon: GraduationCap, label: "Training Report" },
  { to: "/tasks", icon: ListTodo, label: "My Tasks" },
  { to: "/trips", icon: MapPinned, label: "My Trips" },
  { to: "/sales", icon: TrendingUp, label: "My Sales" },
  { to: "/targets", icon: TargetIcon, label: "My Target" },
  { to: "/sales-opportunities", icon: Lightbulb, label: "Opportunities" },
  { to: "/product-coverage", icon: LayoutGrid, label: "Floor Display" },
  { to: "/pos-requests", icon: Truck, label: "POS Requests" },
  { to: "/docs", icon: FileText, label: "Training Documents" },
  { to: "/profile", icon: UserCircle, label: "My Profile" },
];

export default function MobileLayout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (!user) return null;
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-[#F3F7FC] pb-24">
      <header className="sticky top-0 z-30 bg-[#001F54] text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="p-1">
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <BrandLogo className="w-7 h-7 rounded object-contain bg-white/10" />
          <div className="leading-tight">
            <div className="text-[9px] tracking-[0.3em] text-white/50 font-semibold">MLILY</div>
            <div className="text-sm font-black font-heading">Sales Companion</div>
          </div>
        </div>
        <Link to="/profile" className="flex items-center gap-2" data-testid="profile-avatar">
          <NotificationBell />
          <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center text-sm font-bold">
            {user.name?.[0]}
          </div>
        </Link>
      </header>

      {open && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#001F54] text-white flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
            <div className="p-5 border-b border-white/10 flex justify-between items-start">
              <div className="flex items-center gap-3">
                <BrandLogo className="w-10 h-10 rounded-lg object-contain bg-white/10 shrink-0" />
                <div>
                  <div className="text-[9px] tracking-[0.3em] text-white/50 font-semibold">MLILY</div>
                  <div className="text-xl font-black font-heading">CRM Mobile</div>
                  <div className="mt-3 text-sm">{user.name}</div>
                  <div className="text-xs text-white/50 flex items-center gap-2">{user.email} <DemoBadge /></div>
                </div>
              </div>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              {DRAWER_NAV.map(n => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                  data-testid={`drawer-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <n.icon className="w-4 h-4" />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </nav>
            <button onClick={doLogout} className="m-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 justify-center" data-testid="mobile-logout-btn">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      )}

      <main className="p-4 max-w-2xl mx-auto">{children}</main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-5 max-w-2xl mx-auto">
          {BOTTOM_NAV.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-semibold transition ${
                  isActive ? "text-[#003DA5]" : "text-slate-500"
                }`
              }
              data-testid={`bottom-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {n.cta ? (
                <div className={`w-11 h-11 -mt-4 rounded-full bg-[#003DA5] text-white grid place-items-center shadow-lg`}>
                  <n.icon className="w-5 h-5" />
                </div>
              ) : (
                <n.icon className="w-5 h-5" />
              )}
              <span>{n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
